import type { EditorView } from '@tiptap/pm/view';
import type { TiptapDoc } from '../types';

export interface UploadedImage {
    url: string;
    fileId: string;
}

export const ALLOWED_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;

// Grid cells are leaves: an empty cell carries no `src`, so an all-empty grid
// stays empty rather than counting as content.
function isContentNode(node: { type?: string; attrs?: { src?: unknown } }): boolean {
    if (node.type === 'image' || node.type === 'horizontalRule') return true;
    return node.type === 'imageGridCell' && Boolean(node.attrs?.src);
}

/** True when a TiptapDoc has no text content, no images, and no horizontal rules. */
export function isDocEmpty(doc: TiptapDoc): boolean {
    if (!doc?.content || doc.content.length === 0) return true;
    const stack: unknown[] = [...doc.content];
    while (stack.length > 0) {
        const node = stack.pop() as {
            type?: string;
            text?: string;
            attrs?: { src?: unknown };
            content?: unknown[];
        };
        if (!node) continue;
        if (typeof node.text === 'string' && node.text.trim().length > 0) return false;
        if (isContentNode(node)) return false;
        if (Array.isArray(node.content)) stack.push(...node.content);
    }
    return true;
}

/**
 * Map the number of dropped/pasted images to one of the grid extension's
 * built-in layout presets. Caps at the largest preset (2x3); any extra
 * images beyond 6 are dropped silently. Mirrors the layout presets defined
 * in ImageGridView so the toolbar layout-picker stays consistent.
 */
function pickGridLayout(count: number): { rows: number; cols: number } {
    if (count <= 1) return { rows: 1, cols: 1 };
    if (count === 2) return { rows: 1, cols: 2 };
    if (count === 3) return { rows: 1, cols: 3 };
    if (count === 4) return { rows: 2, cols: 2 };
    return { rows: 2, cols: 3 };
}

/** Build evenly distributed column widths summing to 100. */
function buildEvenColWidths(cols: number): number[] {
    const base = Math.floor(100 / cols);
    const widths = Array.from({ length: cols }, () => base);
    widths[cols - 1] += 100 - base * cols;
    return widths;
}

/** Uploads the files in parallel, reporting failures through `setUploadError`; returns the successes. */
export async function uploadImages(
    files: File[],
    uploadFn: (file: File) => Promise<UploadedImage>,
    setUploadError: (s: string | null) => void,
    setIsUploading: (b: boolean) => void
): Promise<UploadedImage[]> {
    setIsUploading(true);
    let uploads: Array<UploadedImage | null>;
    try {
        uploads = await Promise.all(
            files.map(async (file) => {
                try {
                    return await uploadFn(file);
                } catch {
                    return null;
                }
            })
        );
    } finally {
        setIsUploading(false);
    }

    const successful = uploads.filter((u): u is UploadedImage => u !== null);
    if (successful.length === 0) {
        setUploadError('Image upload failed.');
    } else if (successful.length < files.length) {
        setUploadError(`${files.length - successful.length} image(s) failed to upload.`);
    }
    return successful;
}

/** Uploads the images, then inserts one imageGrid at the selection holding the successful ones. */
export async function uploadAndInsertImageGrid(
    view: EditorView,
    files: File[],
    uploadFn: (file: File) => Promise<UploadedImage>,
    setUploadError: (s: string | null) => void,
    setIsUploading: (b: boolean) => void
): Promise<void> {
    const successful = await uploadImages(files, uploadFn, setUploadError, setIsUploading);
    if (successful.length === 0) return;

    const { rows, cols } = pickGridLayout(successful.length);
    const colWidths = buildEvenColWidths(cols);
    const totalCells = rows * cols;

    const cells = Array.from({ length: totalCells }, (_, i) => {
        const u = successful[i];
        return view.state.schema.nodes.imageGridCell.create(
            u ? { src: u.url, fileId: u.fileId } : { src: null, fileId: null }
        );
    });

    const grid = view.state.schema.nodes.imageGrid.create(
        { rows, cols, rowHeight: 140, colWidths },
        cells
    );

    const tr = view.state.tr.replaceSelectionWith(grid);
    view.dispatch(tr);
}
